import { useCallback, useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { DEFAULT_PREFERENCES, type PlayerPreferences } from './types';

const SAVE_DELAY_MS = 400;

/**
 * Player preferences, persisted on the server (User.playerPreferences).
 *
 * Changes apply immediately and are saved after a short pause, so dragging a
 * volume slider sends one request, not fifty. If the server can't be
 * reached the player still works with defaults.
 */
export function usePlayerPreferences() {
  const [prefs, setPrefs] = useState<PlayerPreferences>(DEFAULT_PREFERENCES);
  const [loaded, setLoaded] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const pending = useRef<Partial<PlayerPreferences>>({});
  const timer = useRef<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .playerPreferences()
      .then((p) => !cancelled && setPrefs(p))
      .catch(() => undefined)
      .finally(() => !cancelled && setLoaded(true));
    return () => {
      cancelled = true;
    };
  }, []);

  const flush = useCallback(() => {
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = null;
    const patch = pending.current;
    pending.current = {};
    if (Object.keys(patch).length === 0) return;
    api
      .savePlayerPreferences(patch)
      .then(() => setSaveError(null))
      .catch((err) => setSaveError((err as Error).message));
  }, []);

  const update = useCallback(
    (patch: Partial<PlayerPreferences>) => {
      setPrefs((p) => ({ ...p, ...patch }));
      pending.current = { ...pending.current, ...patch };
      if (timer.current) window.clearTimeout(timer.current);
      timer.current = window.setTimeout(flush, SAVE_DELAY_MS);
    },
    [flush],
  );

  // Never lose a change made just before leaving the player.
  useEffect(() => () => flush(), [flush]);

  return { prefs, update, loaded, saveError };
}
